import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";

interface PillItem {
  id: string;
  value: string;
  text: string;
  href?: string;
  ["data-active"]?: "true" | "false";
  ["aria-current"]?: "true" | "false";
}

interface PillProps {
  type: "category" | "tag" | "category-tag";
  pills: PillItem[];
}

function tryDecodeURI(input: string): string {
  if (!input || input.indexOf("%") === -1) return input;
  try { return decodeURIComponent(input); } catch { return input; }
}

function decodeHtmlOnce(s: string): string {
  return s
    .replace(/&amp;/gi, "&")
    .replace(/&#38;/gi, "&")
    .replace(/&#x26;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

function normalizeLabel(raw: string): string {
  let s = tryDecodeURI(raw ?? "");
  let prev: string;
  do { prev = s; s = decodeHtmlOnce(s); }
  while (s !== prev && /&(amp|#38|#x26|lt|gt|quot|#39);/i.test(s));
  s = s.replace(/&+(?:amp|#38|#x26);/gi, "&").replace(/&&;/g, "&");
  return s.trim().replace(/\s+/g, " ");
}

const PillList: React.FC<PillProps> = ({ pills, type }) => {
  const router = useRouter();
  const ctParam = (router.query["category-tag"] as string) || "";

  const handleAllClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    router.push("/blog", undefined, { shallow: true });
  };

  return (
    <div className="pill-list">
      {pills.map((pill) => {
        const base =
          (pill.text ?? "").toString() ||
          (pill.value ?? "").toString() ||
          (pill.href ?? "").toString();

        const label = normalizeLabel(base);
        const display = label.charAt(0).toUpperCase() + label.slice(1);
        const href = pill.href ?? `/blog?${type}=${encodeURIComponent(label).replace(/%20/g, "%20")}`;
        const fromFlags =
          pill["data-active"] === "true" || pill["aria-current"] === "true";
        const isAll = pill.text?.toLowerCase() === "all";
        const isActive = fromFlags || (isAll && !ctParam);

        const commonProps = {
          className: `pill action-btn${isActive ? " is-active" : ""}`,
          "aria-current": isActive ? "true" : undefined,
          "data-active": isActive ? "true" : undefined,
        } as const;

        if (isAll) {
          return (
            <a key={pill.id} href="/blog" onClick={handleAllClick} {...commonProps}>
              <span>{display}</span>
            </a>
          );
        }
        return (
          <Link key={pill.id} href={href} prefetch={false} {...commonProps}>
            <span>{display}</span>
          </Link>
        );
      })}
    </div>
  );
};

export default PillList;
